<template>
  <div class="mb-6 space-y-6 text-base font-montserrat text-sm">
    <p data-cy="etranscriptcopyone">
      To request an official e-transcript, use either 
      <a 
        :class="`${secondaryColorClass} font-semibold hover:underline`"
        href="https://www.parchment.com/students/order-status/"
        target="_blank"
      >
        Parchment
      </a> or 
      <a 
        :class="`${secondaryColorClass} font-semibold hover:underline`"
        href="https://tsorder.studentclearinghouse.org/school/select"
        target="_blank"
      >
        Student Clearinghouse
      </a>
        to have a transcript sent to 
        <a
          :class="`${secondaryColorClass} font-semibold hover:underline`"
          href="mailto:api@apiexperience.com"
          target="_blank"
        >
          api@apiexperience.com
        </a>
        directly.
    </p>

    <p data-cy="etranscriptcopytwo">
      Your e-transcript should include all courses in which you are currently enrolled. 
      If your current semester/quarter's courses do not appear on your official transcript, please send us an 
      <span class="font-bold italic">additional</span> unofficial transcript that shows your current coursework. 
      To be clear, in the scenario outlined here, we must receive both your official transcript, as well as an additional, 
      unofficial transcript showing current coursework in which you are currently enrolled.
      <br/> <br/>
      <b>IMPORTANT:</b> If you are applying for a later or non-adjacent term, 
      we recommend waiting to order your official e-transcript until it reflects the final grades
       for the courses in which you are currently enrolled. 
       This will prevent you from paying for an additional official transcript once your current courses are complete. 
       For example, if you are in the middle of the fall semester and applying for a fall semester next year, 
       we recommend waiting until your spring semester has started before ordering your official transcript.
    </p>
    <CheckBox
      label="Check this off your to do list here once you send us your transcript."
      label-class="text-base"
      name="eTranscript"
      :checked="checked"
      :validation-errors="v$.value.checkedOption"
      @input="onFieldChange('checkedOption', $event)"
    />
    <ButtonWithSpinner
      ref="SubmitForm"
      type="submit"
      variant="secondary"
      variant-type="block"
      button-height="min-h-[58px] md:min-h-[60px] mt-4"
      :disabled="!allowSubmit"
      :grey-disabled-class="true"
      @click.prevent="submit()"
    >
      <span
        >Submit
        <svg
          v-if="formSubmitted && !hasErrors"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="inline feather feather-check"
        >
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      </span>
    </ButtonWithSpinner>

    <p v-if="hasErrors" class="text-error-900">
      Please complete the required fields correctly.
    </p>
    <div v-if="formioErrors" class="text-error-900">
      <p v-for="error in formioErrors" :key="error">
        {{ error }}
      </p>
    </div>
    <div v-if="missingAppId" class="text-error-900">
      <p>
        No Application ID, please contact your API program manager to help you
        with your application.
      </p>
    </div>
    <div v-if="formSubmitted && !hasErrors" class="text-success-900">
      <p>
        Submission Complete, thank you!
      </p>
    </div>
  </div>
</template>

<script>
import CheckBox from "../SharedComponents/CheckBox.vue";
import ButtonWithSpinner from "../SharedComponents/ButtonWithSpinner.vue";
import { sameAs } from "@vuelidate/validators";
import formInputs from "../../../mixins/formInputs";
import formValidation from "../../../mixins/formValidation";
import formioHelpers from "../../../mixins/formioHelpers";
import formIoApi from "../../../mixins/formIoApi";
import { mapState } from "vuex";
import useVuelidate from "@vuelidate/core";
export default {
  name: "ETranscriptForm",
  components: { CheckBox, ButtonWithSpinner },
  mixins: [formInputs, formValidation, formioHelpers, formIoApi],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      applicationId: "",
      formSubmitted: false,
      checked: false,
      value: {
        checkedOption: false,
      },
    };
  },
  computed: {
    ...mapState(["formioToken"]),
    allowSubmit() {
      return this.value.checkedOption;
    },
    createSubmissionDataForFormIo() {
      const body = {
        data: {
          application_id: this.applicationId,
          eTranscriptSubmitted: true,
        },
      };
      return body;
    },
    patchSubmissionDataForFormIo() {
      const body = [
        {
          op: "add",
          path: "/data/eTranscriptSubmitted",
          value: true,
        },
      ];
      return body;
    },
    formPath() {
      return "studentapplicationstage2step1";
    },
  },
  async created() {
    //set the application ID
    this.applicationId = this.setApplicationId;
    await this.prefetchFormData();
  },

  methods: {
    prefetchFormData() {
      this.formioSubmissionExists(this.formPath, this.applicationId).then(
        (formSubmissionId) => {
          if (formSubmissionId) {
            this.getFormioSubmission(this.formPath, formSubmissionId).then(
              (response) => {
                this.checked = response.eTranscriptSubmitted;
                this.loading = false;
              }
            );
          } else {
            this.loading = false;
          }
        }
      );
    },
    async submit() {
      this.submitted = false;
      //calling the startLoading method of the button, which enables the button to loading state
      this.$refs.SubmitForm.startLoading();
      try {
        await this.checkForApplicationId();
        if (this.allowSubmit) {
          if (this.submission_id) {
            this.submitToFormIo(
              this.formPath,
              this.patchSubmissionDataForFormIo,
              "PATCH",
              this.submission_id
            ).then((response) => {
              if (response) {
                this.formSubmitted = true;
              }
              this.$refs.SubmitForm.stopLoading();
            });
          } else {
            this.submitToFormIo(
              this.formPath,
              this.createSubmissionDataForFormIo
            ).then((response) => {
              if (response) {
                this.submission_id = response;
                this.formSubmitted = true;
              }
              this.$refs.SubmitForm.stopLoading();
            });
          }
        } else {
          this.$refs.SubmitForm.stopLoading();
        }
      } catch {
        this.$refs.SubmitForm.stopLoading();
      }
    },
  },
  validations: {
    value: {
      checkedOption: {
        sameAs: sameAs(true),
      },
    },
  },
};
</script>
